import { RefObject, useEffect, useRef, useState } from "react"

/**
 * 检查 DOM 元素的内容是否发生溢出
 * 并使用 ResizeObserver 监听元素尺寸变化以重新检查
 * @returns [isOverflowing, ref]
 * - isOverflowing: boolean 文本是否溢出
 * - ref: React.RefObject<T | null> 需要绑定的 DOM 引用
 */
const useIsOverflow = <T extends HTMLElement = HTMLDivElement>(): [boolean, RefObject<T | null>] => {
  const ref = useRef<T>(null)
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)

  const getLineClamp = (el: HTMLElement) => {
    const style = window.getComputedStyle(el)
    const clampRaw =
      (style as { webkitLineClamp?: string; lineClamp?: string }).webkitLineClamp ||
      (style as { lineClamp?: string }).lineClamp
    const clamp = clampRaw ? parseInt(clampRaw, 10) : NaN
    return Number.isFinite(clamp) && clamp > 0 ? clamp : undefined
  }

  const isElementOverflowing = (el: HTMLElement) => {
    if (el.clientHeight === 0 && el.clientWidth === 0) return false
    return el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
  }

  const checkOverflow = () => {
    const element = ref.current
    if (!element) return
    const child = element.firstElementChild as HTMLElement | null
    const childHasClamp = child ? getLineClamp(child) !== undefined : false
    let overflowing = childHasClamp ? isElementOverflowing(child!) : isElementOverflowing(element)
    if (!overflowing && child && !childHasClamp) {
      overflowing = isElementOverflowing(child)
    }
    setIsOverflowing(overflowing)
  }

  useEffect(() => {
    // 文本或组件更新时，重新检查一次
    checkOverflow()

    const element = ref.current
    if (!element) return

    // 实例化 ResizeObserver 并监听元素
    const observer = new ResizeObserver(checkOverflow)
    observer.observe(element)
    const child = element.firstElementChild as HTMLElement | null
    if (child && child !== element) {
      observer.observe(child)
    }

    // 清理函数：在组件卸载或 effect 重新运行时停止监听
    return () => {
      observer.unobserve(element)
      if (child && child !== element) {
        observer.unobserve(child)
      }
      observer.disconnect()
    }
  }, []) // 尺寸变化时由 ResizeObserver 触发

  useEffect(() => {
    // 组件每次渲染后复查，处理文本变化但尺寸未变的情况
    checkOverflow()
  })

  return [isOverflowing, ref]
}

export default useIsOverflow
